import type {TimelineJobRow} from './job-rows'
import type {TerminalRowEntry} from './terminal-jobs'
import type {ScheduleDate, ViewRange} from './types'
import {businessNowHour, spanStartMinutes} from './work-day'

export type ScheduleUrgencyTier = 'imminent' | 'today' | 'future' | 'continuation'

export type ScheduleUrgency = {
    tier: ScheduleUrgencyTier
}

export type UrgencyContext = {
    visibleDays: ScheduleDate[]
    viewRange: ViewRange
    nowHour?: number
}

const IMMINENT_WINDOW_MINUTES = 60

export function resolveNowHour(now: Date = new Date()): number {
    return businessNowHour(now)
}

export function minutesUntilTimedStart(
    job: TimelineJobRow,
    viewDayOffset: number,
    nowHour: number,
    options?: {isToday?: boolean},
): number | null {
    if (job.scheduleKind !== 'timed' || !job.span) {
        return null
    }
    const isToday = options?.isToday ?? viewDayOffset === 0
    if (!isToday) {
        return null
    }

    return spanStartMinutes(job.span) - nowHour * 60
}

export function isTimedStartingSoon(
    job: TimelineJobRow,
    viewDayOffset: number,
    nowHour: number,
    options?: {withinMinutes?: number; isToday?: boolean},
): boolean {
    const withinMinutes = options?.withinMinutes ?? IMMINENT_WINDOW_MINUTES
    const minutesUntil = minutesUntilTimedStart(job, viewDayOffset, nowHour, options)
    if (minutesUntil === null) {
        return false
    }
    return minutesUntil <= withinMinutes
}

export function formatRelativeStartAnchor(minutesUntil: number): string {
    if (minutesUntil <= 0) {
        return 'Started'
    }
    if (minutesUntil <= 30) {
        return `Starts in ${minutesUntil}m`
    }
    if (minutesUntil <= 60) {
        return `Starts in ${minutesUntil}m`
    }
    return ''
}

export function getScheduleUrgency(
    entry: TerminalRowEntry,
    context: UrgencyContext,
): ScheduleUrgency {
    const {job, viewDayOffset, isContinuationDay, display} = entry
    const day = context.visibleDays[viewDayOffset]
    const isToday = day?.isToday ?? false
    const nowHour = context.nowHour ?? 10

    if (isContinuationDay && display.missingLabels.length === 0 && job.status === 'assigned') {
        return {tier: 'continuation'}
    }

    if (job.scheduleKind === 'timed' && isToday) {
        const minutesUntil = minutesUntilTimedStart(job, viewDayOffset, nowHour)
        if (minutesUntil !== null && minutesUntil > 0 && minutesUntil <= IMMINENT_WINDOW_MINUTES) {
            return {tier: 'imminent'}
        }
    }

    if (isToday) {
        return {tier: 'today'}
    }

    if (isContinuationDay) {
        return {tier: 'continuation'}
    }

    return {tier: 'future'}
}

export function scheduleUrgencyClass(tier: ScheduleUrgencyTier): string {
    switch (tier) {
        case 'imminent':
            return 'scheduleImminent'
        case 'today':
            return 'scheduleToday'
        case 'future':
            return 'scheduleFuture'
        case 'continuation':
            return 'scheduleContinuation'
    }
}
